import { useState } from 'react';
import { useRoute } from 'app/hooks/navigation';
import { useDefaultListingLocation } from 'app/network/listing';
import type { SpecificRouteProp } from 'app/types/navigation';

type AllListingsParams = SpecificRouteProp<'all-listings'>['params'];
type DefaultListingLocation = NonNullable<ReturnType<typeof useDefaultListingLocation>['data']>;

interface AppliedGuestLocation {
  appliedDistance: number;
  appliedLatitude: number | undefined;
  appliedLocationLabel: string | undefined;
  appliedLongitude: number | undefined;
}

export interface GuestLocationDraft {
  address: string;
  distance: number;
  location?: { latitude: number; longitude: number };
}

function getAppliedGuestLocation(
  params: AllListingsParams | undefined,
  defaultLocation: DefaultListingLocation | undefined,
): AppliedGuestLocation {
  return {
    appliedDistance: params?.searchDistance ?? defaultLocation?.searchDistance ?? 0,
    appliedLatitude: params?.searchCenterLatitude ?? defaultLocation?.latitude,
    appliedLocationLabel: params?.searchLocationLabel,
    appliedLongitude: params?.searchCenterLongitude ?? defaultLocation?.longitude,
  };
}

function toGuestCoordinates(appliedLatitude: number | undefined, appliedLongitude: number | undefined) {
  return appliedLatitude !== undefined && appliedLongitude !== undefined
    ? { latitude: appliedLatitude, longitude: appliedLongitude }
    : undefined;
}

function appliedGuestLocationChanged(current: AppliedGuestLocation, next: AppliedGuestLocation) {
  return (
    current.appliedDistance !== next.appliedDistance ||
    current.appliedLatitude !== next.appliedLatitude ||
    current.appliedLocationLabel !== next.appliedLocationLabel ||
    current.appliedLongitude !== next.appliedLongitude
  );
}

export function useGuestLocationDraft(initialDraft?: GuestLocationDraft) {
  const route = useRoute<'all-listings'>();
  const { data: defaultLocation } = useDefaultListingLocation();
  const appliedLocation = getAppliedGuestLocation(route.params, defaultLocation);
  const { appliedDistance, appliedLatitude, appliedLocationLabel, appliedLongitude } = appliedLocation;
  const [draftSource, setDraftSource] = useState(appliedLocation);
  const [draftAddress, setDraftAddress] = useState(initialDraft?.address ?? appliedLocationLabel ?? '');
  const [draftDistance, setDraftDistance] = useState<number>(initialDraft?.distance ?? appliedDistance);
  const [draftLocation, setDraftLocation] = useState<{ latitude: number; longitude: number } | undefined>(
    () => initialDraft?.location ?? toGuestCoordinates(appliedLatitude, appliedLongitude),
  );

  if (appliedGuestLocationChanged(draftSource, appliedLocation)) {
    setDraftSource(appliedLocation);
    setDraftDistance(appliedDistance);
    setDraftLocation(toGuestCoordinates(appliedLatitude, appliedLongitude));
    setDraftAddress(appliedLocationLabel ?? '');
  }

  return {
    appliedLatitude,
    appliedLongitude,
    defaultLocation,
    draftAddress,
    draftDistance,
    draftLocation,
    setDraftAddress,
    setDraftDistance,
    setDraftLocation,
    resetToApplied: () => {
      setDraftAddress(appliedLocationLabel ?? '');
      setDraftDistance(appliedDistance);
      setDraftLocation(toGuestCoordinates(appliedLatitude, appliedLongitude));
    },
  };
}
